import { useContext, useState } from "react";
import { Loader2, Repeat } from "lucide-react";
import AuthContext from "../context/AuthContext";
import { submitRsvp } from "../api/EventAPI";
import { formatEventRange, myRsvpStatus, repeatsLabel } from "../utils/eventDomain";
import { announceEventChange, applyChangeToInvites, useEventChanges } from "../utils/eventChanges";
import { inviteDetails, inviteKey, isOccurrenceInvite, withoutInvite } from "../utils/eventInvites";
import { answerOccurrence } from "../utils/occurrenceActions";

// Readable local-time formatting via the same native Date primitive already
// used elsewhere in Calendar/EventFocusModal, just with display options
// instead of the bare toLocaleString() call — no new date library.
function formatEventDateTime(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

// Modal content for the Calendar page's Inbox button. `invites` is fetched
// and owned by Calendar.js (so the unanswered badge is accurate before this
// modal is ever opened) — this component only renders it and handles
// responding. GET /events/invites/ only ever returns unanswered invites, so
// once one is answered it's removed locally rather than shown with a status
// label (there's no "answered" state this endpoint would ever return).
//
// Rows are series or occurrence invitations (utils/eventInvites), identified
// by (scope, id). Each is answered at its own endpoint and resolves only
// itself: answering a series invitation never answers an occurrence one, or
// the other way round.
export default function EventInviteInbox({
  invites,
  loading,
  error,
  onInviteAnswered,
  onEventRemoved,
  onClose,
  openModal,
}) {
  const { token } = useContext(AuthContext);
  const [respondingIds, setRespondingIds] = useState([]);
  const [actionError, setActionError] = useState("");

  // `invites` is a snapshot captured when this modal was opened (ModalManager
  // stores plain props in a stack, it doesn't re-render this modal when
  // Calendar's own invites state changes later). Mirrors the same
  // local-copy pattern EventFocusModal uses, so responding to one invite
  // updates this list immediately instead of only on next open.
  const [localInvites, setLocalInvites] = useState(invites);

  // Answers given elsewhere — an occurrence or series detail opened from
  // here, or another page — resolve their own rows here too (each scope
  // resolves only itself).
  useEventChanges((change) => setLocalInvites((prev) => applyChangeToInvites(prev, change)));

  const removeInvite = (invite) => {
    setLocalInvites((prev) => withoutInvite(prev, invite));
  };

  const handleRespond = async (invite, status) => {
    const key = inviteKey(invite);
    if (respondingIds.includes(key)) return;

    setActionError("");
    setRespondingIds((prev) => [...prev, key]);
    try {
      if (isOccurrenceInvite(invite)) {
        // This occurrence only (announced by answerOccurrence).
        const { state } = await answerOccurrence(invite.occurrence, { status }, token);
        removeInvite(invite);
        onInviteAnswered?.({ invite, eventId: invite.occurrence.event_id, status: state.status });
      } else {
        const data = await submitRsvp(invite.event.id, status, token);
        removeInvite(invite);
        onInviteAnswered?.({ invite, eventId: invite.event.id, status: data.status });
        // Accepting adds the event to Calendar (and possibly Home's next event).
        announceEventChange({ eventId: invite.event.id, kind: "updated", seriesRsvp: data });
      }
    } catch (err) {
      console.error("Failed to respond to event invite", { invite: key, err });
      setActionError("Couldn't update your RSVP. Please try again.");
    } finally {
      setRespondingIds((prev) => prev.filter((id) => id !== key));
    }
  };

  // Reuses the existing Event focus view rather than duplicating event
  // display here — invite.event is already the exact shape EventFocusModal
  // expects. If the user RSVPs (or removes the event entirely) from inside
  // that modal instead of this row, mirror the same local cleanup here.
  // An occurrence invitation opens its occurrence: no parent Event access is
  // needed (EventFocusModal resolves it with loadEventDetailAccess), and its
  // answer there comes back through useEventChanges above.
  const handleOpenEvent = (invite) => {
    if (isOccurrenceInvite(invite)) {
      openModal("EventFocusModal", { occurrence: invite.occurrence });
      return;
    }
    openModal("EventFocusModal", {
      event: invite.event,
      onUpdated: (updatedEvent) => {
        const status = myRsvpStatus(updatedEvent);
        if (status && status !== "invited") {
          removeInvite(invite);
          onInviteAnswered?.({ invite, eventId: updatedEvent.id, status });
        }
      },
      onRemoved: (removedEventId) => {
        removeInvite(invite);
        onEventRemoved?.(removedEventId);
      },
    });
  };

  return (
    <div
      className="bg-white rounded-lg shadow-lg w-full max-w-md max-h-[85vh] flex flex-col"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="px-5 py-4 border-b border-gray-200 flex-shrink-0">
        <h2 className="text-lg font-bold">Event Invitations</h2>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-3">
        {loading && localInvites.length === 0 && (
          <div className="flex items-center justify-center py-10 text-gray-400">
            <Loader2 size={22} className="animate-spin" />
          </div>
        )}

        {error && <p className="text-sm text-red-600 py-2">{error}</p>}

        {!loading && !error && localInvites.length === 0 && (
          <p className="text-sm text-gray-500 py-6 text-center">No event invitations.</p>
        )}

        {actionError && <p className="text-xs text-red-600 mb-2">{actionError}</p>}

        <ul className="divide-y divide-gray-100">
          {localInvites.map((invite) => {
            const isResponding = respondingIds.includes(inviteKey(invite));
            const details = inviteDetails(invite);
            // A series says how it repeats; an occurrence invitation shows
            // nothing of its series (its recurrence is masked anyway).
            const recurrence = isOccurrenceInvite(invite) ? null : repeatsLabel(invite.event?.recurrence);
            return (
              <li key={inviteKey(invite)} className="py-3">
                <button
                  type="button"
                  onClick={() => handleOpenEvent(invite)}
                  className="text-left w-full"
                >
                  <p className="font-semibold text-sm hover:underline">{details.title}</p>
                </button>

                {details.is_all_day ? (
                  // All-day days are read in the event's timezone.
                  <p className="text-xs text-gray-600 mt-0.5">
                    {formatEventRange(details.starts_at, details.ends_at, true, details.timezone)}
                  </p>
                ) : (
                  <>
                    <p className="text-xs text-gray-600 mt-0.5">
                      {formatEventDateTime(details.starts_at)}
                    </p>
                    <p className="text-xs text-gray-600">
                      to {formatEventDateTime(details.ends_at)}
                    </p>
                  </>
                )}
                {recurrence && (
                  <p className="flex items-center gap-1 text-xs font-medium text-gray-500 mt-0.5">
                    <Repeat size={12} aria-hidden="true" />
                    {recurrence}
                  </p>
                )}
                {details.hostedBy && (
                  <p className="text-xs text-gray-500 mt-1">Hosted by {details.hostedBy}</p>
                )}
                <p className="text-xs text-gray-500">Invited by {invite.invited_by_username}</p>

                <div className="flex gap-2 mt-2">
                  <button
                    type="button"
                    disabled={isResponding}
                    onClick={() => handleRespond(invite, "going")}
                    className="flex-1 rounded-full border border-gray-300 px-3 py-1.5 text-xs hover:bg-gray-50 disabled:opacity-50"
                  >
                    Going
                  </button>
                  <button
                    type="button"
                    disabled={isResponding}
                    onClick={() => handleRespond(invite, "declined")}
                    className="flex-1 rounded-full border border-gray-300 px-3 py-1.5 text-xs hover:bg-gray-50 disabled:opacity-50"
                  >
                    Decline
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
